import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ROLE_LABELS, STATUS_COLORS, STEPS } from "@/lib/steps";
import { PlusCircle, Search, ChevronRight, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { hasRole } from "@/lib/utils";

export default function Programs() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  // Search text lives in the URL (?q=...) so it survives opening a program and coming Back.
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const setQ = (v) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (v) next.set("q", v); else next.delete("q");
    return next;
  }, { replace: true });
  const [deleteTarget, setDeleteTarget] = useState(null); // program being deleted

  const load = () => {
    api.get("/programs")
      .then(({ data }) => setPrograms(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => { load(); }, []);

  const filtered = programs.filter((p) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return p.training_title.toLowerCase().includes(t) || p.client_name.toLowerCase().includes(t) || p.training_topic.toLowerCase().includes(t);
  });

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto page-enter">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">All Programs</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight mt-1">Programs</h1>
        </div>
        {hasRole(user, "bd") && (
          <Link to="/programs/new"><Button data-testid="programs-new-btn"><PlusCircle className="w-4 h-4 mr-2" /> New Program</Button></Link>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="programs-search" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client, title, topic..." className="pl-9" />
        </div>
      </div>

      <Card className="mt-6 border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Program</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Current Step</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`programs-row-${p.id}`} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="font-medium">{p.training_title}</div>
                    <div className="text-xs text-muted-foreground">{p.training_topic}</div>
                  </TableCell>
                  <TableCell>{p.client_name}</TableCell>
                  <TableCell>
                    <div className="text-sm">{p.current_step}/{STEPS.length}</div>
                    <div className="text-xs text-muted-foreground">{p.current_step_info?.title}</div>
                  </TableCell>
                  <TableCell><span className="text-sm">{ROLE_LABELS[p.current_step_info?.owner] || "-"}</span></TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_COLORS[p.status === "closed" ? "completed" : "in_progress"]}>
                      {p.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {hasRole(user) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`programs-delete-${p.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteTarget(p)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Link to={`/programs/${p.id}`}>
                        <Button variant="ghost" size="sm">Open <ChevronRight className="w-4 h-4 ml-1" /></Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">No programs found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DeleteProgramDialog
        program={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); load(); }}
      />
    </div>
  );
}

function DeleteProgramDialog({ program, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (program) setPassword(""); }, [program]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.delete(`/programs/${program.id}`, { data: { password } });
      toast.success(`Program "${program.training_title}" deleted`);
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={program !== null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Program</DialogTitle>
          <DialogDescription>
            This permanently deletes <span className="font-medium text-foreground">{program?.training_title}</span> and
            everything tied to it (steps, comments, emails, attachments). This cannot be undone. Enter your password to confirm.
          </DialogDescription>
        </DialogHeader>
        <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (password && !loading) submit(); }}>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Your Password</Label>
            <Input
              data-testid="delete-program-password"
              type="password"
              name="delete-confirm-password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="destructive" data-testid="delete-program-submit" disabled={loading || !password}>
              <Trash2 className="w-4 h-4 mr-2" /> {loading ? "Deleting…" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
